/**
 * The cascade renderer — the whole nav-rail tree from the node it is given
 * down, as one indented column. It has two mounts and draws the same thing
 * in both: inside the puck's overlay below `md` (navigation-rail-puck.ts),
 * and inline as the rail's own column at `md` and up (navigation-rail.ts).
 * Only the topmost rail anyone can see mounts either, so a nested rail's
 * rows reach the screen exclusively through this renderer, indented under
 * the row that owns them.
 *
 * A row whose item carries a registered child rail expands IN PLACE — the
 * child level renders indented directly beneath it — rather than replacing
 * this level: there is no back button and no level-replacement, so the user
 * always sees a level and its open children together. Because
 * `navigation-rail.ts`'s own `items()` sets `childNode` only on the
 * currently-active item at each level (`item.active ? navRailActiveChild : null`),
 * at most one row per level can ever be expandable, and it is always the one
 * the app is actually routed into — so the tray for the active chain is open
 * by default (`deriveActiveChain` below), and the chevron affordance exists
 * to let the user collapse it back to a flat overview, not to choose among
 * several equally-expandable siblings (there is only ever one).
 *
 * That is also why activating a row is never the end of the drill on its own.
 * The tree is discovered from what is mounted, so a destination the app is
 * not routed into carries no level yet — one it will never carry and one
 * whose rail simply has not mounted read exactly alike. The drill therefore
 * navigates and STAYS OPEN across that navigation (`activated$` below),
 * watching the registry for a level the destination mounts underneath its
 * owner. One arrives and the drill unfolds it, so the operator chooses inside
 * the level they asked for instead of being handed whichever of its
 * destinations mounted first; none arrives inside `LEVEL_ARRIVAL_WINDOW_MS`
 * and the destination was a leaf, which is the only reading under which the
 * drill has nothing left to show and dismisses.
 *
 * Activation dispatches `navigate` on the rail that OWNS the item — the
 * element whose own light DOM holds it — so a projected row is
 * indistinguishable from clicking the real item: same event, same target,
 * same consumer listener. Dispatching on whoever mounted this instead is
 * what made every level past the root unroutable, since the mount lives
 * inside the ROOT rail and a deeper value names none of the root consumer's
 * own destinations. The row's own `navigate` is stopped at the tile for the
 * same reason — one navigate leaves this renderer, and it carries the
 * owning rail as its target.
 *
 * Width and padding belong to the mount, not here: the renderer fills
 * whatever box it is given and never declares its own measure — the puck's
 * `show()` call and the rail's own column each declare that box, which is
 * how one renderer serves an overlay on a phone and a fixed column on a
 * desktop without knowing which it is in. Which of the item's two anatomies
 * the rows carry travels the same way, as `compact` from the mount: `expanded`
 * (icon then label, left aligned) wherever there is room, because a tree read
 * as a list wants rows, and the stacked 80dp one where the mount has said
 * there is not.
 *
 * Items render as bare `<schmancy-navigation-rail-item>` — not wrapped in a
 * `<schmancy-navigation-rail>` — deliberately: a rail nested here would
 * register itself into the very tree this is drawing, giving the level it
 * projects a phantom child of its own. That also means the item's own
 * roving-tabindex (only the active item gets `tabindex="0"`, everyone else
 * `-1`) has no ancestor rail moving focus among them, so `updated()` below
 * re-asserts `tabindex="0"` on every currently-rendered row after each
 * render, making every row an ordinary Tab stop.
 *
 * `footer` draws the node's own `footer` slot beneath the rows, and `header`
 * draws its `sheet-header` slot above them — only the puck's overlay asks for
 * either, because below `md` it is the only navigation on screen and the
 * rail's column, with its sign-out and its mark, is not. `sheet-header` is a
 * slot the rail itself never draws: on desktop the column already carries its
 * mark at its head and its plate as its last row, so the consumer hands the
 * phone sheet a header row of its own there rather than this sheet moving
 * the column's children out from under it. The held elements are MOVED in,
 * not copied: a copy carries neither the listeners its consumer bound nor a
 * name set as a property, so the control in the sheet would announce and do
 * nothing. Each leaves a marker where it stood in the rail and returns to it
 * when this sheet disconnects. While held they follow the rail's own children:
 * an element the consumer's template stops rendering leaves the sheet at once
 * and is not put back, and one it starts rendering joins the sheet.
 *
 * Activating a button or link among them ends the drill as choosing a leaf
 * does, read a microtask after the click so a consumer's `preventDefault()`
 * keeps it open — a menu item's own contract. A click on anything else held
 * (a separator inside a wrapper), or on a control that opens a popup of its
 * own (`aria-haspopup`, `aria-expanded`), leaves the drill open: closing it
 * would take the popup's anchor back into a column that is off screen.
 */
import { reveal } from '@schmancy/directives'
import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { asapScheduler, BehaviorSubject, EMPTY, merge, Observable, Subject, timer } from 'rxjs'
import { distinctUntilChanged, filter, map, observeOn, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import type { NavRailItemDescriptor, RegisteredRail } from './context'
import './navigation-rail-item'
import { navRailChildren, navRailTreeChanged$ } from './registry'

/**
 * How long a destination has to reveal a level of its own before the drill
 * reads it as a leaf and dismisses. Measured in a consuming admin shell at
 * 375px: a section rail registers 108ms after its row is activated warm and
 * 124ms on the first, lazily-imported visit. Roughly double that, so a level
 * still arrives on a slower device, and short enough that a real leaf's
 * dismiss still reads as the tap's own consequence rather than a stall.
 */
const LEVEL_ARRIVAL_WINDOW_MS = 400

/** What activating a held element means: a control that does something, not a field that takes input. */
const ACTIVATING_CONTROL =
	'button, a[href], input[type="button"], input[type="submit"], [role="button"], [role="link"], [role="menuitem"]'

/**
 * A row the drill activated and is now waiting on. `before` is the set of
 * children `owner` already carried at that moment, so the level that ARRIVES
 * is told apart from the outgoing one — the rail of the surface being
 * navigated away from, still registered until that surface tears down.
 */
type ActivatedRow = {
	readonly owner: RegisteredRail
	readonly value: string
	readonly before: ReadonlySet<RegisteredRail>
}

/** The level `row`'s own navigation revealed — `null` while none has. */
function arrivedLevel(row: ActivatedRow): RegisteredRail | null {
	return navRailChildren(row.owner).find(child => !row.before.has(child)) ?? null
}

/**
 * The chain of currently-active item values, one per depth, down to the
 * deepest level whose active item still has a further child registered.
 * Seeds `expandedPath` so a drill opened while routed three levels deep
 * (admin → settings → legal) shows that whole chain already unfolded.
 */
function deriveActiveChain(node: RegisteredRail): string[] {
	const active = node
		.items()
		.find(
			(item): item is NavRailItemDescriptor & { childNode: RegisteredRail } => item.active && item.childNode !== null,
		)
	return active ? [active.value, ...deriveActiveChain(active.childNode)] : []
}

@customElement('schmancy-navigation-rail-sheet')
export class SchmancyNavigationRailSheet extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				/* Every level below the first queries the level above it for room
				 * before spending an indent; the FIRST level has no such level and
				 * would query whatever container happens to sit outside this
				 * component — an overlay, a rail, a page — or, with none, never
				 * match at all. The host is that level's own container, so the
				 * measure each level asks about is always the box it was actually
				 * handed. */
				container-type: inline-size;
			}
		`,
	]

	/** The level this drill starts at — every level below it is drawn too. */
	@property({ attribute: false })
	node!: RegisteredRail

	/**
	 * Whether a row may show its label as a tooltip at all — the mounting
	 * rail's own `showTooltips`, forwarded because this renderer draws the
	 * rows that rail's items describe. It never means one appears: each row
	 * shows one only while its own label is clipped by the measure its mount
	 * declared (navigation-rail-item.ts's `labelClipped`).
	 */
	@property({ type: Boolean })
	showTooltips = true

	/**
	 * Draw every row in the item's 80dp anatomy — icon above a centred label —
	 * instead of the cascade's own row one. The mount decides, exactly as it
	 * decides the box: the rail turns this on for the compact column a narrow
	 * frame gets (navigation-rail.ts's `compactColumn`), and the puck's overlay
	 * never does, because an overlay has all the width it asks for.
	 *
	 * The label stays rendered, clipped to the 64px of content an 80dp column
	 * leaves, which is what keeps the row's own tooltip answerable: it appears
	 * only while the label is genuinely cut (navigation-rail-item.ts's
	 * `labelClipped`), so the compact column is the one place it earns its
	 * keep on nearly every row.
	 */
	@property({ type: Boolean })
	compact = false

	/**
	 * Draw the node's `footer` slot beneath the rows (see the file doc). Off by
	 * default, and a node with nothing in that slot draws a sheet identical to
	 * one that never asked.
	 */
	@property({ type: Boolean })
	footer = false

	/**
	 * Draw the node's `sheet-header` slot above the rows, as one row: the first
	 * element leads, the last trails (see the file doc). Off by default, and a
	 * node with nothing in that slot draws a sheet identical to one that never
	 * asked.
	 */
	@property({ type: Boolean })
	header = false

	/** The rail whose slotted elements this sheet holds, and which slots — `null` while it holds none. */
	private readonly heldSource$ = new BehaviorSubject<{ rail: RegisteredRail; slots: readonly string[] } | null>(null)

	private readonly heldActivated$ = new Subject<Event>()

	/** The held slots that currently hold at least one element. */
	@state()
	private holding: ReadonlySet<string> = new Set()

	/** `expandedPath[d]` is the value of the row expanded at depth `d` — at
	 *  most one per level (see the file doc comment for why), absent once
	 *  the user collapses that level back to a flat list. */
	@state()
	private expandedPath: string[] = []

	/** The chain `expandedPath` was last seeded from — the app's own routed
	 *  position, so a user collapse can be told apart from the app moving. */
	private activeChain: string[] = []

	/** The row whose navigation this drill is following, `null` when idle. */
	@state()
	private following: ActivatedRow | null = null

	private readonly activated$ = new Subject<ActivatedRow>()

	connectedCallback() {
		super.connectedCallback()

		merge(
			navRailTreeChanged$.pipe(tap(() => this.requestUpdate())),

			// The drill stays open across the navigation an activated row starts
			// and watches for the level that destination mounts. `switchMap`
			// because only the newest activation's outcome matters — a second row
			// chosen while the first is still mounting cancels the first's window
			// rather than closing the drill out from under it.
			this.activated$.pipe(
				tap(row => {
					this.following = row
				}),
				switchMap(row =>
					merge(
						navRailTreeChanged$.pipe(
							map(() => arrivedLevel(row)),
							filter(level => level !== null),
						),
						timer(LEVEL_ARRIVAL_WINDOW_MS).pipe(map(() => null)),
					).pipe(
						take(1),
						map(level => ({ row, level })),
					),
				),
				tap(({ row, level }) => {
					this.following = null
					// A level arrived: `willUpdate` below unfolds it, since the app's
					// own routed chain now runs through this row. None did, so the
					// destination was a leaf and the drill has nothing left to show.
					if (level === null) {
						this.dispatchEvent(new CustomEvent('close', { detail: row.value, bubbles: true, composed: true }))
					}
				}),
			),

			// A marker a consumer's re-render removed means that template stopped
			// rendering the element, so it leaves the sheet and is never put back
			// beside whatever replaced it. The rail's child list is watched because
			// that re-render can land while the sheet is still open.
			this.heldSource$.pipe(
				distinctUntilChanged(
					(previous, next) =>
						previous?.rail === next?.rail && previous?.slots.join() === next?.slots.join(),
				),
				switchMap(source =>
					source === null
						? EMPTY
						: new Observable<never>(() => {
								const { rail, slots } = source
								const held = new Map<Element, Comment>()
								const sync = () => {
									held.forEach((marker, element) => {
										if (marker.parentNode === rail) return
										held.delete(element)
										element.remove()
									})
									Array.from(rail.children)
										.filter(child => slots.includes(child.slot))
										.forEach(child => {
											const marker = document.createComment(`schmancy-navigation-rail ${child.slot}`)
											child.replaceWith(marker)
											held.set(child, marker)
										})
									// The sheet's order is rebuilt from the markers' order in the rail on
									// every pass, back to front, so each element sits before the one that
									// follows it there. Only an element out of place moves — moving one
									// takes focus off it.
									const byMarker = new Map<Node, Element>(Array.from(held, ([element, marker]) => [marker, element]))
									Array.from(rail.childNodes)
										.flatMap(node => byMarker.get(node) ?? [])
										.reduceRight<Element | null>((next, element) => {
											if (element.parentNode !== this || element.nextSibling !== next) this.insertBefore(element, next)
											return element
										}, null)
									this.holding = new Set(Array.from(held.keys(), element => element.slot))
								}
								sync()
								const railChildren = new MutationObserver(sync)
								railChildren.observe(rail, { childList: true })
								return () => {
									railChildren.disconnect()
									held.forEach((marker, element) => {
										if (marker.parentNode === rail) marker.replaceWith(element)
										else element.remove()
									})
									this.holding = new Set()
								}
							}),
				),
			),

			this.heldActivated$.pipe(
				// The composed path empties once dispatch ends, so what was clicked is
				// read here, before the microtask: from the target up to the held
				// element slotted into this sheet.
				map(event => {
					const path = event.composedPath().filter((node): node is Element => node instanceof Element)
					const within = path.slice(0, path.findIndex(element => element.parentNode === this) + 1)
					return {
						event,
						activates:
							within.some(element => element.matches(ACTIVATING_CONTROL)) &&
							!within.some(
								element =>
									element.hasAttribute('aria-expanded') ||
									(element.getAttribute('aria-haspopup') ?? 'false') !== 'false',
							),
					}
				}),
				observeOn(asapScheduler),
				filter(({ event, activates }) => activates && !event.defaultPrevented),
				tap(() => this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	protected willUpdate(changed: PropertyValues) {
		super.willUpdate(changed)

		const slots = [...(this.header ? ['sheet-header'] : []), ...(this.footer ? ['footer'] : [])]
		this.heldSource$.next(slots.length > 0 ? { rail: this.node, slots } : null)

		// While a row's navigation is being followed the drill holds the tray it
		// had: the tree is mid-move, and the chain derived from it still runs
		// through the level the app is on its way out of.
		if (this.following !== null) return

		// The routed chain is the default unfold, and the app moving to a new
		// one is the only thing that overrides a collapse the user asked for —
		// re-seeding on every repaint would spring a collapsed level back open
		// the moment anything else in the tree twitched.
		const chain = deriveActiveChain(this.node)
		if (chain.length !== this.activeChain.length || chain.some((value, depth) => value !== this.activeChain[depth])) {
			this.activeChain = chain
			this.expandedPath = chain
		}
	}

	protected updated(changed: PropertyValues) {
		super.updated(changed)
		this.renderRoot
			.querySelectorAll('schmancy-navigation-rail-item')
			.forEach(item => item.setAttribute('tabindex', '0'))
	}

	private handleSelect(item: NavRailItemDescriptor, owner: RegisteredRail, depth: number) {
		// The level is already on screen — this is a pure expand/collapse of it,
		// and the app is already routed into it, so there is nothing to navigate.
		if (item.childNode) {
			this.expandedPath =
				this.expandedPath[depth] === item.value
					? this.expandedPath.slice(0, depth)
					: [...this.expandedPath.slice(0, depth), item.value]
			return
		}

		// An ACTIVE row with no level is one the registry has already answered:
		// its own surface is mounted and registered no rail. The puck's overlay
		// resolves and dismisses on this — a destination was chosen, and there is
		// no level left to wait for.
		if (item.active) {
			owner.dispatchEvent(new CustomEvent('navigate', { detail: item.value, bubbles: true, composed: true }))
			this.dispatchEvent(new CustomEvent('close', { detail: item.value, bubbles: true, composed: true }))
			return
		}

		// Any other row may or may not carry a level — the tree cannot say until
		// that destination's surface is mounted. Announced BEFORE the dispatch,
		// because a destination whose surface mounts synchronously registers its
		// rail inside `dispatchEvent` itself.
		this.activated$.next({ owner, value: item.value, before: new Set(navRailChildren(owner)) })
		owner.dispatchEvent(new CustomEvent('navigate', { detail: item.value, bubbles: true, composed: true }))
	}

	/**
	 * `revealed` is the level's own open state, and `reveal()` is what makes it
	 * motion rather than a mount: the rows stay in the DOM either way, so the
	 * box animates between 0 and its natural height with nothing beneath it
	 * jumping, a re-entrant toggle reverses from wherever the last one had got
	 * to, and the directive's own first update is a state rather than a
	 * transition — which is what keeps a surface that loads three levels deep
	 * already unfolded on first paint instead of playing an expand on arrival.
	 *
	 * `smooth` is the disclosure register (`snappy` is the 300ms button one,
	 * `bouncy` an overshoot an operator would ride several times an hour): its
	 * spring has travelled ~94% by 190ms and spends the rest settling, so the
	 * level reads immediate without ever snapping. The root passes `true` and
	 * stays open forever — a cascade's first level discloses nothing.
	 */
	private renderLevel(owner: RegisteredRail | null, depth: number, revealed: boolean) {
		if (owner === null) return nothing

		return html`
			<div
				${reveal(revealed, { preset: 'smooth' })}
				role="list"
				class=${this.classMap({
					// grid-cols-1 is minmax(0,1fr) where a bare `grid` gives an auto
					// track. A rail item never wraps its label, so an auto track sizes
					// to the whole intrinsic width of the longest one and every row
					// spills the box it was handed. Every level and every row below
					// carries it, because a grid ITEM's own `min-width: auto` would
					// re-impose the same floor one level down.
					'@container grid grid-cols-1 gap-1': true,
					// The indent comes out of the row's own width on top of that, so a
					// level spends one only where the row still has room to read; each
					// level is its own container, so the indent stops itself the moment
					// a level runs out, and a box narrower than the threshold gets depth
					// from the chevron and the grouping instead. The rule down the
					// level's leading edge is the containment itself: indent alone says
					// a row is offset, the line says WHICH row it hangs from, and with
					// four or five siblings between one level and the next that is the
					// difference between a tree and a stagger.
					'@min-[80px]:ps-3 @min-[80px]:border-s @min-[80px]:border-outline-variant/25': depth > 0,
				})}
			>
				${repeat(
					owner.items(),
					item => item.value,
					item => {
						// For the row whose own navigation the drill is following, the
						// level is the one that ARRIVED since it was activated: until the
						// outgoing surface tears down, `childNode` still names that
						// surface's rail, and drawing it here offers the operator the
						// level they just left under the row they just chose.
						const row =
							this.following?.owner === owner && this.following.value === item.value
								? { ...item, childNode: arrivedLevel(this.following) }
								: item
						const expandable = row.childNode !== null
						const expanded = this.expandedPath[depth] === row.value
						return html`
							<div role="listitem" class="grid grid-cols-1 gap-1">
								<div class="flex items-center">
									<schmancy-navigation-rail-item
										?expanded=${!this.compact}
										class="block min-w-0 flex-1"
										value=${row.value}
										label=${row.label}
										icon=${ifDefined(row.icon)}
										tooltip-text=${this.showTooltips ? row.label : ''}
										.ariaExpanded=${expandable ? (expanded ? 'true' : 'false') : null}
										.active=${row.active}
										@navigate=${(event: Event) => {
											event.stopPropagation()
											this.handleSelect(row, owner, depth)
										}}
									></schmancy-navigation-rail-item>
									${when(
										expandable,
										() => html`
											<schmancy-icon
												aria-hidden="true"
												class=${this.classMap({
													'shrink-0 text-xs text-surface-onVariant/70': true,
													// The level's own Blackbird smooth register, curve
													// included — same 500ms, same spring reveal() runs the
													// box on — so the marker and the box it marks read as
													// one gesture rather than two timings that merely
													// overlap. Measured on that preset's cubic-bezier
													// FALLBACK instead, the chevron peaked at 197.6° beside
													// a level whose own overshoot is content-clipped away.
													'transition-transform duration-500 ease-spring-smooth motion-reduce:transition-none': true,
													'rotate-180': expanded,
												})}
											>
												keyboard_arrow_down
											</schmancy-icon>
										`,
									)}
								</div>
								${this.renderLevel(row.childNode, depth + 1, expanded)}
							</div>
						`
					},
				)}
			</div>
		`
	}

	protected render() {
		// The header is one row, its first element leading and its last trailing —
		// a mark then a plate. pb-3 is the gap the rows keep from it, a step wider
		// than the rows' own gap-1 so the row reads as the sheet's head rather than
		// a first destination. pt-2 and items-start are the wide column's own
		// footer, so a footer reads the same beneath the sheet's rows as beneath
		// the column's.
		return html`
			${when(
				this.holding.has('sheet-header'),
				() => html`
					<div class="flex items-center justify-between gap-2 pb-3" part="header">
						<slot name="sheet-header" @click=${(event: Event) => this.heldActivated$.next(event)}></slot>
					</div>
				`,
			)}
			${this.renderLevel(this.node, 0, true)}
			${when(
				this.holding.has('footer'),
				() => html`
					<div class="flex flex-col items-start gap-1 pt-2" part="footer">
						<slot name="footer" @click=${(event: Event) => this.heldActivated$.next(event)}></slot>
					</div>
				`,
			)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-navigation-rail-sheet': SchmancyNavigationRailSheet
	}
}
